// ═══════════════════════════════════════════════════════════════
// Baumhaselring 180 — expose format (bilingual EN + DE)
// Modelled on bdx.expose-info.de/post/grammestrasse/grammestrasse-2.html
// Compact, structured, 10 numbered sections. English default; the
// top navbar language toggle switches to German.
// ═══════════════════════════════════════════════════════════════

import { useEffect } from "react";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { openCalendly } from "@/lib/calendly";
import { getWhatsAppUrl } from "@/lib/whatsapp";
import { useLang } from "@/lib/i18n";
import { useTheme } from "@/lib/theme";
import Img from "@/components/Img";
import HeroPreload from "@/components/HeroPreload";
import { Lightbox, useLightbox } from "@/components/Lightbox";
import { webpSrc, bestSrcFor } from "@/lib/webpSrc";

// ─── Copy (EN + DE) ─────────────────────────────────────────
const COPY = {
  en: {
    addressLine: "Baumhaselring 180, 14469 Potsdam-Golm",
    heroTitle: "3-room condominium",
    heroSub:  "Potsdam-Golm · Campus-adjacent",
    kpi: [
      { label: "Purchase price",     value: "€333,000",    emphasis: true },
      { label: "Gross yield",        value: "5.95 %",      emphasis: true },
      { label: "Target rent",        value: "€1,650/mo" },
      { label: "Equity",             value: "€25,537" },
      { label: "Total investment",   value: "€358,537" },
      { label: "Status",             value: "Available" },
    ],
    sections: {
      narrative: "Sales narrative",
      finance:   "Investment & financing",
      objekt:    "Property & building data",
      wirtschaft:"Operating economics & fees",
      sanierung: "Refurbishment scope",
      photos:    "Impressions",
      standort:  "Location story",
      lage:      "Location",
      model:     "Interactive model",
      contact:   "Contact",
    },
    narrative: [
      {
        n: 1,
        title: "5.95 % gross yield from day one",
        body:  "At a target cold rent of €1,650/mo (€1,600 apartment + €50 parking) the asset produces solid cash flow from the first month. Yield sits meaningfully above what new-builds in Potsdam currently deliver.",
        chips: ["5.95 % gross yield", "€1,650 target rent", "Cash-flow day one"],
      },
      {
        n: 2,
        title: "Modernisation creates value — not the purchase price alone",
        body:  "The 1995 existing-stock building is handed over vacant. Value here is built not by paying a new-build margin, but by repositioning through modernisation, furniture package and rental placement.",
        chips: ["Built 1995", "Existing stock", "Delivered vacant"],
      },
      {
        n: 3,
        title: "Campus-adjacent demand secures rentability",
        body:  "Baumhaselring 180 sits at the Eiche-Golm interface — 400 m from the University of Potsdam's largest campus and the Potsdam Science Park. 9,000 students & doctoral candidates plus 2,000 researchers in Golm underpin demand.",
        chips: ["1.3 % vacancy Potsdam", "9,000 students", "€14.04/m² median rent"],
      },
    ],
    finanzierung: [
      ["Purchase price · apartment",   "€305,000"],
      ["Purchase price · parking",     "€20,000"],
      ["Purchase price · furniture",   "€8,000"],
      ["Total purchase price",         "€333,000", true],
      ["Ancillary costs (est.)",       "€25,537"],
      ["Total investment",             "€358,537", true],
      ["Equity",                       "€25,537 (ancillary only)"],
      ["Loan amount",                  "€358,537 (100 % + costs financed)"],
      ["Rate / amortisation",          "4.90 % / 1.30 %"],
      ["Target cold rent",             "€1,650/mo"],
    ],
    taxBox: {
      eyebrow: "Tax optimisation — dual-contract model",
      body:    "Refurbishment costs can be deducted as Werbungskosten under §6 Abs 1 Nr 1a EStG — provided they stay below the 15 % threshold within 3 years. Materially reduces effective equity lock-up.",
      rows: [
        ["Assumed tax rate",  "42 %"],
        ["Building basis (AfA)", "€292,500"],
      ],
    },
    exitBox: {
      eyebrow: "Exit after 10 years — tax-free",
      note:    "Model at +3 % capital appreciation p.a., non-binding.",
      rows: [
        ["Value at +3 % p.a.",  "≈ €447,000"],
        ["Remaining loan · 10 y", "− €308,000"],
        ["Sale proceeds",       "≈ €139,000"],
      ],
    },
    objektdaten: [
      ["Living area",       "53.57 m²"],
      ["Rooms",             "3.0"],
      ["Floor",             "Ground"],
      ["Year built",        "1995"],
      ["Heating",           "Central"],
      ["Energy certificate", "D · demand-based"],
      ["Parking",           "1 · plus cellar unit"],
      ["Letting",           "Self or property mgmt."],
    ],
    wirtschaft: [
      ["Total HOA (mo.)",       "€60"],
      ["Property management (mo.)", "€80"],
      ["Cold rent · target",    "€1,650/mo"],
      ["Linear AfA (2 %)",      "€5,850 p.a."],
      ["Furniture AfA (10 y)",  "€800 p.a."],
      ["Appreciation (model)",  "3.0 % p.a."],
    ],
    sanierung: {
      cols: ["Category", "Scope · per contract"],
      rows: [
        ["Floors & surfaces", "Detail per works contract to follow."],
        ["Bathroom",          "Bathroom modernisation — per works contract."],
        ["Electrical & tech", "Per works contract to follow."],
        ["Walls & windows",   "Per works contract to follow."],
        ["Furnishings",       "Built-in kitchen + €8,000 furniture package."],
      ],
    },
    photoDisclaimer: "The last three images show reference modernisations (bath, bedroom, hallway) — not the current unit.",
    standort: [
      "Potsdam-Golm is Brandenburg's largest science location: 3 Max Planck Institutes, 2 Fraunhofer Institutes and 40+ research companies. 9,000 students & doctoral candidates plus 2,000 scientists work here daily.",
      "Baumhaselring 180 sits at the Eiche-Golm interface — 400 m from the University of Potsdam campus and Potsdam Science Park. So the address has a very clearly defined target-tenant base.",
      "Golm station connects the location directly: Potsdam Hbf ~10 min, Berlin Hbf ~40 min (RB23), BER airport ~30 min (RB22).",
    ],
    stats: [
      { v: "1.3 %",       l: "Vacancy · Potsdam" },
      { v: "€14.04/m²",   l: "Median rent 2025" },
      { v: "+13.8 %",     l: "Age 19–25 by 2040" },
    ],
    lage: {
      address: "Baumhaselring 180, 14469 Potsdam · Golm/Eiche district",
    },
    model: {
      body: "Want to run your own scenario — equity share, interest rate, appreciation? The full calculator with sliders and 10-year table is offloaded so this overview stays fast and readable.",
      cta:  "Open calculator",
    },
    contact: {
      heading: "Interested in this asset?",
      body:    "Book a viewing or request the full Baumhaselring 180 documentation.",
      cta1:    "Book viewing →",
      cta2:    "WhatsApp",
      name:    "Arslan Gokiev",
      title:   "Managing Director · IRE Berlin",
      waMsg:   "Interest in Baumhaselring 180 — please send documentation.",
    },
    disclaimer: "All figures are model calculations and marketing summaries, not investment or tax advice. Tax treatment depends on personal circumstances and requires review by a tax adviser.",
    lightboxLabels: {
      livingAsIs: "Living room · as-is",
      kitchenAsIs: "Kitchen · as-is",
      bathAsIs:    "Bathroom · as-is",
      bedroomAsIs: "Bedroom · as-is",
      balcony:     "Balcony · green outlook",
      entrance:    "Entrance · staircase",
      facade:      "Building · front",
      streetscape: "Streetscape",
      facadeSide:  "Building · side",
      bathAfter:    "Bathroom · reference (modernised)",
      bedroomAfter: "Bedroom · reference (modernised)",
      hallwayAfter: "Hallway · reference (modernised)",
    },
  },
  de: {
    addressLine: "Baumhaselring 180, 14469 Potsdam-Golm",
    heroTitle: "3-Zimmer-Eigentumswohnung",
    heroSub:  "Potsdam-Golm · Campus-adjacent",
    kpi: [
      { label: "Kaufpreis",         value: "€333,000",    emphasis: true },
      { label: "Bruttorendite",     value: "5.95 %",      emphasis: true },
      { label: "Soll-Miete",        value: "€1,650 p.M." },
      { label: "Eigenkapital",      value: "€25,537" },
      { label: "Gesamtinvestition", value: "€358,537" },
      { label: "Status",            value: "Frei" },
    ],
    sections: {
      narrative: "Verkaufsnarrative",
      finance:   "Investition & Finanzierung",
      objekt:    "Objekt- & Gebäudedaten",
      wirtschaft:"Wirtschaftsdaten & Hausgeld",
      sanierung: "Sanierungsmaßnahmen Wohnung",
      photos:    "Impressionen",
      standort:  "Standort-Story",
      lage:      "Lage",
      model:     "Interaktives Modell",
      contact:   "Kontakt",
    },
    narrative: [
      {
        n: 1,
        title: "5.95 % Bruttorendite von Beginn an",
        body:  "Bei einer Soll-Kaltmiete von €1,650 im Monat (Wohnung €1,600 + Stellplatz €50) erwirtschaftet das Objekt vom ersten Tag an einen soliden Cashflow. Die Rendite liegt deutlich über dem, was Neubauten in Potsdam aktuell erzielen.",
        chips: ["5.95 % Bruttorendite", "€1,650 Soll-Miete", "Cashflow ab Tag 1"],
      },
      {
        n: 2,
        title: "Modernisierung schafft Wert — nicht der Kaufpreis allein",
        body:  "Der Bestandsbau von 1995 wird leerstehend übergeben. Werterhalt entsteht hier nicht durch das Zahlen einer Neubau-Marge, sondern durch eigenes Reposition durch Modernisierung, Möblierung und Vermietungspositionierung.",
        chips: ["Baujahr 1995", "Bestandsimmobilie", "Vakant erworben"],
      },
      {
        n: 3,
        title: "Campus-nahe Nachfrage sichert Vermietbarkeit",
        body:  "Baumhaselring 180 liegt am Eiche-Golm-Übergang — 400 m vom größten Standort der Universität Potsdam und dem Potsdam Science Park. 9,000 Studierende und Promovierende + 2,000 Forschende in Golm sichern die Nachfrage.",
        chips: ["1.3 % Leerstand Potsdam", "9,000 Studierende", "€14.04/m² Medianmiete"],
      },
    ],
    finanzierung: [
      ["Kaufpreis Wohnung",            "€305,000"],
      ["Kaufpreis Stellplatz",         "€20,000"],
      ["Kaufpreis Möbelpaket",         "€8,000"],
      ["Gesamt Kaufpreis",             "€333,000", true],
      ["Kaufnebenkosten (geschätzt)",  "€25,537"],
      ["Gesamtinvestition",            "€358,537", true],
      ["Eigenkapital",                 "€25,537 (nur NK)"],
      ["Kreditsumme",                  "€358,537 (100 % + NK finanziert)"],
      ["Zins / Tilgung",               "4.90 % / 1.30 %"],
      ["Soll-Miete (Kalt)",            "€1,650 p.M."],
    ],
    taxBox: {
      eyebrow: "Steuer-Optimierung — 2-Verträge-Modell",
      body:    "Sanierungskosten können als Werbungskosten unter §6 Abs 1 Nr 1a EStG abgesetzt werden — solange sie unter der 15%-Schwelle innerhalb 3 Jahren bleiben. Reduziert die effektive Eigenkapitalbindung erheblich.",
      rows: [
        ["Angenommener Steuersatz", "42 %"],
        ["Building basis (AfA)",    "€292,500"],
      ],
    },
    exitBox: {
      eyebrow: "Exit nach 10 Jahren — steuerfrei",
      note:    "Modellrechnung bei +3 % Wertentwicklung p.a., unverbindlich.",
      rows: [
        ["Wert bei +3 % p.a.",  "≈ €447,000"],
        ["Restschuld nach 10 J.", "− €308,000"],
        ["Verkaufserlös",       "≈ €139,000"],
      ],
    },
    objektdaten: [
      ["Wohnfläche",         "53.57 m²"],
      ["Zimmeranzahl",       "3.0"],
      ["Etage",              "EG"],
      ["Baujahr Gebäude",    "1995"],
      ["Heizungsart",        "Zentralheizung"],
      ["Energieausweis",     "D · Bedarfsausweis"],
      ["Stellplatz",         "1 · zzgl. Kellerraum"],
      ["Vermietung",         "eigen oder Verwaltung"],
    ],
    wirtschaft: [
      ["Hausgeld gesamt (p.M.)",  "€60"],
      ["Verwaltungskosten (p.M.)","€80"],
      ["Nettokaltmiete (Soll)",   "€1,650 p.M."],
      ["AfA linear (2 %)",        "€5,850 p.a."],
      ["Möbel-AfA (10 J.)",       "€800 p.a."],
      ["Wertsteigerung (Modell)", "3.0 % p.a."],
    ],
    sanierung: {
      cols: ["Kategorie", "Maßnahmen laut Werkvertrag"],
      rows: [
        ["Böden & Oberflächen", "Detailangaben laut Werkvertrag folgen."],
        ["Sanitärbereich",      "Modernisierung Bad — Details laut Werkvertrag."],
        ["Elektrik & Technik",  "Details laut Werkvertrag folgen."],
        ["Wände & Fenster",     "Details laut Werkvertrag folgen."],
        ["Möblierung",          "Einbauküche + Möbelpaket €8,000."],
      ],
    },
    photoDisclaimer: "Die letzten drei Bilder zeigen Referenz-Modernisierungen (Bad, Schlafzimmer, Flur) — nicht das aktuelle Objekt.",
    standort: [
      "Potsdam-Golm ist Brandenburgs größter Wissenschaftsstandort: 3 Max-Planck-Institute, 2 Fraunhofer-Institute und über 40 Forschungsfirmen. 9,000 Studierende und Promovierende sowie 2,000 Wissenschaftler arbeiten hier täglich.",
      "Baumhaselring 180 sitzt am Eiche-Golm-Übergang — 400 m vom Campus der Universität Potsdam und dem Potsdam Science Park. Die Adresse hat also eine sehr klar definierte Ziel-Mieter-Basis.",
      "Golm-Bahnhof verbindet den Standort direkt: Potsdam Hbf ~10 min, Berlin Hbf ~40 min (RB23), BER Flughafen ~30 min (RB22).",
    ],
    stats: [
      { v: "1.3 %",     l: "Leerstand Potsdam" },
      { v: "€14.04/m²", l: "Medianmiete 2025" },
      { v: "+13.8 %",   l: "Alter 19–25 bis 2040" },
    ],
    lage: {
      address: "Baumhaselring 180, 14469 Potsdam · Ortsteil Golm/Eiche",
    },
    model: {
      body: "Möchten Sie Ihr eigenes Szenario durchrechnen — Eigenkapitalquote, Zinssatz, Wertsteigerung? Der komplette Rechner mit Reglern und Zehn-Jahres-Tabelle ist ausgelagert, um diese Übersicht schnell und lesbar zu halten.",
      cta:  "Rechner öffnen",
    },
    contact: {
      heading: "Interesse an diesem Objekt?",
      body:    "Vereinbaren Sie eine Besichtigung oder fordern Sie die vollständigen Unterlagen zur Baumhaselring 180 an.",
      cta1:    "Besichtigung buchen →",
      cta2:    "WhatsApp",
      name:    "Arslan Gokiev",
      title:   "Geschäftsführung IRE Berlin",
      waMsg:   "Interesse an Baumhaselring 180 — Unterlagen anfordern.",
    },
    disclaimer: "Alle Angaben sind Modellrechnungen und Marketing-Zusammenfassungen, keine Anlage- oder Steuerberatung. Steuerliche Behandlung hängt von persönlichen Umständen ab und erfordert Prüfung durch einen Steuerberater.",
    lightboxLabels: {
      livingAsIs:   "Wohnzimmer · Bestand",
      kitchenAsIs:  "Küche · Bestand",
      bathAsIs:     "Bad · Bestand",
      bedroomAsIs:  "Schlafzimmer · Bestand",
      balcony:      "Balkon · Grüner Ausblick",
      entrance:     "Eingang · Treppenhaus",
      facade:       "Gebäude · Vorderseite",
      streetscape:  "Straßenansicht",
      facadeSide:   "Gebäude · Seite",
      bathAfter:    "Bad · Referenz (modernisiert)",
      bedroomAfter: "Schlafzimmer · Referenz (modernisiert)",
      hallwayAfter: "Flur · Referenz (modernisiert)",
    },
  },
};

// ─── Theme tokens ───────────────────────────────────────────
const THEMES = {
  dark: {
    page:       "bg-ire-black text-white",
    heroGrad:   "bg-gradient-to-b from-ire-black/40 via-ire-black/30 to-ire-black",
    heroSubAddr:"text-white/60",
    heroTitle:  "text-white",
    heroSub:    "text-ire-accent",
    kpiStrip:   "border-white/[0.08] bg-white/[0.02] divide-white/[0.06]",
    kpiLabel:   "text-white/40",
    kpiValue:   "text-white/90",
    kpiEmph:    "text-ire-accent",
    sectionBorder: "border-white/[0.06]",
    eyebrow:    "text-ire-accent",
    h2:         "text-white",
    body:       "text-white/60",
    bodyDim:    "text-white/50",
    card:       "border-white/[0.08] bg-white/[0.02]",
    rowBorder:  "border-white/[0.05]",
    rowLabel:   "text-white/55",
    rowValue:   "text-white/90",
    rowEmph:    "text-ire-accent bg-ire-accent/[0.04]",
    chipBg:     "bg-ire-accent/10 text-ire-accent border-ire-accent/25",
    accentBox:  "border-ire-accent/30 bg-ire-accent/[0.04]",
    tblHead:    "text-white/40 border-white/[0.08]",
    tblRow:     "text-white/70 border-white/[0.04]",
    tblRowSub:  "text-white/55",
    photoTint:  "",
    photoFooter:"text-white/35",
    statBox:    "border-white/[0.08]",
    statVal:    "text-ire-accent",
    statLbl:    "text-white/50",
    ctaPrimary: "bg-ire-accent text-ire-black hover:bg-white",
    ctaSecondary:"border-white/30 text-white hover:border-ire-accent hover:text-ire-accent",
    ctaOutline: "border-ire-accent/50 text-ire-accent hover:bg-ire-accent hover:text-ire-black",
    contactName:"text-white",
    contactTitle:"text-white/45",
    linkGold:   "text-ire-accent",
    linkDim:    "text-white/60",
    disclaimer: "text-white/25 border-white/[0.04]",
    mapFilter:  "grayscale",
    lightboxOverlay:"from-ire-black/70",
  },
  light: {
    page:       "bg-[#F7F3EC] text-stone-900",
    heroGrad:   "bg-gradient-to-b from-[#F7F3EC]/10 via-[#F7F3EC]/30 to-[#F7F3EC]",
    heroSubAddr:"text-stone-500",
    heroTitle:  "text-stone-900",
    heroSub:    "text-[#8B6A2E]",
    kpiStrip:   "border-stone-200 bg-white divide-stone-200",
    kpiLabel:   "text-stone-400",
    kpiValue:   "text-stone-900",
    kpiEmph:    "text-[#8B6A2E]",
    sectionBorder: "border-stone-200",
    eyebrow:    "text-[#8B6A2E]",
    h2:         "text-stone-900",
    body:       "text-stone-600",
    bodyDim:    "text-stone-500",
    card:       "border-stone-200 bg-white",
    rowBorder:  "border-stone-200",
    rowLabel:   "text-stone-500",
    rowValue:   "text-stone-900",
    rowEmph:    "text-[#8B6A2E] bg-[#8B6A2E]/[0.06]",
    chipBg:     "bg-[#8B6A2E]/10 text-[#8B6A2E] border-[#8B6A2E]/25",
    accentBox:  "border-[#8B6A2E]/30 bg-[#8B6A2E]/[0.05]",
    tblHead:    "text-stone-500 border-stone-200",
    tblRow:     "text-stone-700 border-stone-100",
    tblRowSub:  "text-stone-500",
    photoTint:  "[filter:sepia(0.06)_saturate(0.95)_brightness(1.02)]",
    photoFooter:"text-stone-400",
    statBox:    "border-stone-200 bg-white",
    statVal:    "text-[#8B6A2E]",
    statLbl:    "text-stone-500",
    ctaPrimary: "bg-[#0F0E0C] text-[#D4AF6A] hover:bg-[#8B6A2E] hover:text-white",
    ctaSecondary:"border-stone-400 text-stone-700 hover:border-[#8B6A2E] hover:text-[#8B6A2E]",
    ctaOutline: "border-[#8B6A2E]/60 text-[#8B6A2E] bg-white hover:bg-[#0F0E0C] hover:text-[#D4AF6A] hover:border-[#0F0E0C]",
    contactName:"text-stone-900",
    contactTitle:"text-stone-500",
    linkGold:   "text-[#8B6A2E]",
    linkDim:    "text-stone-600",
    disclaimer: "text-stone-400 border-stone-200",
    mapFilter:  "",
    lightboxOverlay:"from-black/50",
  },
};

// ─── Presentational helpers ─────────────────────────────────
const Section = ({ n, title, children, testId, T }) => (
  <section className={`border-t pt-10 md:pt-14 ${T.sectionBorder}`} data-testid={testId}>
    <div className={`font-mono text-[10px] tracking-[0.4em] uppercase mb-4 ${T.eyebrow}`}>
      {String(n).padStart(2, "0")}
    </div>
    <h2 className={`font-heading text-[1.5rem] leading-[1.2] sm:text-3xl md:text-4xl mb-8 ${T.h2}`}>
      {title}
    </h2>
    {children}
  </section>
);

const Row = ({ label, value, emphasis, T }) => (
  <div className={`flex items-baseline justify-between gap-3 py-3 border-b ${T.rowBorder} ${emphasis ? `${T.rowEmph} px-3` : ""}`}>
    <span className={`font-body text-[13px] ${T.rowLabel}`}>{label}</span>
    <span className={`font-mono text-sm tabular-nums text-right ${emphasis ? T.kpiEmph : T.rowValue}`}>{value}</span>
  </div>
);

// Photo helpers (existing WebP assets)
const IMG = {
  facade:       webpSrc("/assets/baumhaselring/img-01.jpg"),
  streetscape:  webpSrc("/assets/baumhaselring/img-02.jpg"),
  facadeArched: webpSrc("/assets/baumhaselring/img-03.jpg"),
  balcony:      webpSrc("/assets/baumhaselring/img-04.jpg"),
  bathAfter:    webpSrc("/assets/baumhaselring/img-05.jpg"),
  bedroomAfter: webpSrc("/assets/baumhaselring/img-06.jpg"),
  hallwayAfter: webpSrc("/assets/baumhaselring/img-07.jpg"),
  bedroomAsIs:  webpSrc("/assets/baumhaselring/img-08.jpg"),
  kitchenAsIs:  webpSrc("/assets/baumhaselring/img-10.jpg"),
  bathAsIs:     webpSrc("/assets/baumhaselring/img-11.jpg"),
  livingAsIs:   webpSrc("/assets/baumhaselring/img-12.jpg"),
  entrance:     webpSrc("/assets/baumhaselring/img-13.jpg"),
};

// ─── Page ───────────────────────────────────────────────────
const BaumhaselringPage = () => {
  const { lang } = useLang();
  const { theme } = useTheme();
  const T = COPY[lang === "de" ? "de" : "en"];
  const S = THEMES[theme === "light" ? "light" : "dark"];

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);

  const galleryImages = [
    { src: IMG.livingAsIs,   full: bestSrcFor("/assets/baumhaselring/img-12.jpg"), label: T.lightboxLabels.livingAsIs },
    { src: IMG.kitchenAsIs,  full: bestSrcFor("/assets/baumhaselring/img-10.jpg"), label: T.lightboxLabels.kitchenAsIs },
    { src: IMG.bathAsIs,     full: bestSrcFor("/assets/baumhaselring/img-11.jpg"), label: T.lightboxLabels.bathAsIs },
    { src: IMG.bedroomAsIs,  full: bestSrcFor("/assets/baumhaselring/img-08.jpg"), label: T.lightboxLabels.bedroomAsIs },
    { src: IMG.balcony,      full: bestSrcFor("/assets/baumhaselring/img-04.jpg"), label: T.lightboxLabels.balcony },
    { src: IMG.entrance,     full: bestSrcFor("/assets/baumhaselring/img-13.jpg"), label: T.lightboxLabels.entrance },
    { src: IMG.facade,       full: bestSrcFor("/assets/baumhaselring/img-01.jpg"), label: T.lightboxLabels.facade },
    { src: IMG.streetscape,  full: bestSrcFor("/assets/baumhaselring/img-02.jpg"), label: T.lightboxLabels.streetscape },
    { src: IMG.facadeArched, full: bestSrcFor("/assets/baumhaselring/img-03.jpg"), label: T.lightboxLabels.facadeSide },
    { src: IMG.bathAfter,    full: bestSrcFor("/assets/baumhaselring/img-05.jpg"), label: T.lightboxLabels.bathAfter },
    { src: IMG.bedroomAfter, full: bestSrcFor("/assets/baumhaselring/img-06.jpg"), label: T.lightboxLabels.bedroomAfter },
    { src: IMG.hallwayAfter, full: bestSrcFor("/assets/baumhaselring/img-07.jpg"), label: T.lightboxLabels.hallwayAfter },
  ];
  const lb = useLightbox(galleryImages);

  return (
    <div className={`min-h-screen transition-colors duration-500 ${S.page}`} data-testid="baumhaselring-page">
      <HeroPreload src="/assets/baumhaselring/img-01.jpg" sizes="100vw" />
      <Lightbox images={galleryImages} {...lb} />

      {/* Hero: address + KPI strip */}
      <section className="relative pt-24 md:pt-32" data-testid="baum-hero">
        <div className="relative aspect-[16/10] md:aspect-[21/9] max-h-[560px] overflow-hidden">
          <Img
            src="/assets/baumhaselring/img-01.jpg"
            alt="Baumhaselring 180 — front elevation"
            sizes="100vw"
            loading="eager"
            fetchPriority="high"
            className={`absolute inset-0 w-full h-full object-cover ${S.photoTint}`}
          />
          <div className={`absolute inset-0 ${S.heroGrad}`} />
        </div>

        <div className="max-w-[1400px] mx-auto px-5 md:px-16 lg:px-24 -mt-16 md:-mt-24 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className={`flex items-center gap-2 font-body text-[13px] mb-3 ${S.heroSubAddr}`}>
              <MapPin className={`w-4 h-4 ${S.eyebrow}`} /> {T.addressLine}
            </div>
            <h1 className={`font-heading text-[1.75rem] sm:text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-8 ${S.heroTitle}`}>
              {T.heroTitle}
              <br />
              <span className={`italic font-light ${S.heroSub}`}>{T.heroSub}</span>
            </h1>

            {/* Pinned KPI strip */}
            <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 border divide-y divide-x-0 md:divide-x md:divide-y-0 ${S.kpiStrip}`} data-testid="baum-kpi-strip">
              {T.kpi.map((k) => (
                <div key={k.label} className="p-4 md:p-5">
                  <div className={`font-body text-[9px] tracking-[0.25em] uppercase ${S.kpiLabel}`}>{k.label}</div>
                  <div className={`font-mono text-base md:text-lg tabular-nums mt-1.5 ${k.emphasis ? S.kpiEmph : S.kpiValue}`}>{k.value}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <div className="max-w-[1200px] mx-auto px-5 md:px-16 lg:px-24 mt-16 md:mt-24 space-y-12 md:space-y-16">

        {/* 01 Sales narrative */}
        <Section n={1} title={T.sections.narrative} testId="baum-narrative" T={S}>
          <div className="space-y-8">
            {T.narrative.map((s) => (
              <div key={s.n} className={`border-l-2 pl-5 md:pl-6 ${S.eyebrow.replace("text-", "border-")}/60`}>
                <div className={`font-mono text-[11px] mb-2 ${S.eyebrow}`}>{s.n}</div>
                <h3 className={`font-heading text-lg md:text-xl mb-3 ${S.h2}`}>{s.title}</h3>
                <p className={`font-body text-[14px] md:text-[15px] leading-[1.7] mb-4 ${S.body}`}>{s.body}</p>
                <div className="flex flex-wrap gap-2">
                  {s.chips.map((c) => (
                    <span key={c} className={`inline-block border px-3 py-1.5 font-body text-[11px] tracking-[0.1em] ${S.chipBg}`}>
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* 02 Investment & financing */}
        <Section n={2} title={T.sections.finance} testId="baum-finance" T={S}>
          <div className={`border p-4 md:p-6 ${S.card}`}>
            {T.finanzierung.map(([l, v, e]) => <Row key={l} label={l} value={v} emphasis={e} T={S} />)}
          </div>
          <div className={`mt-6 border p-5 md:p-6 ${S.accentBox}`}>
            <div className={`font-body text-[10px] tracking-[0.3em] uppercase mb-3 ${S.eyebrow}`}>{T.taxBox.eyebrow}</div>
            <p className={`font-body text-[14px] leading-[1.7] mb-4 ${S.body}`}>{T.taxBox.body}</p>
            <div className="grid grid-cols-2 gap-3 text-[13px]">
              {T.taxBox.rows.map(([l, v]) => <Row key={l} label={l} value={v} T={S} />)}
            </div>
          </div>
          <div className={`mt-4 border p-5 md:p-6 ${S.card}`}>
            <div className={`font-body text-[10px] tracking-[0.3em] uppercase mb-3 ${S.eyebrow}`}>{T.exitBox.eyebrow}</div>
            <p className={`font-body text-[13px] leading-[1.7] mb-3 ${S.bodyDim}`}>{T.exitBox.note}</p>
            <div className="space-y-0">
              {T.exitBox.rows.map(([l, v], i) => (
                <Row key={l} label={l} value={v} emphasis={i === T.exitBox.rows.length - 1} T={S} />
              ))}
            </div>
          </div>
        </Section>

        {/* 03 Property & building data */}
        <Section n={3} title={T.sections.objekt} testId="baum-objekt" T={S}>
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0 border p-4 md:p-6 ${S.card}`}>
            {T.objektdaten.map(([l, v]) => <Row key={l} label={l} value={v} T={S} />)}
          </div>
        </Section>

        {/* 04 Operating economics */}
        <Section n={4} title={T.sections.wirtschaft} testId="baum-wirtschaft" T={S}>
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-0 border p-4 md:p-6 ${S.card}`}>
            {T.wirtschaft.map(([l, v]) => <Row key={l} label={l} value={v} T={S} />)}
          </div>
        </Section>

        {/* 05 Refurbishment scope */}
        <Section n={5} title={T.sections.sanierung} testId="baum-sanierung" T={S}>
          <div className={`border overflow-hidden ${S.card}`}>
            <table className="w-full text-left">
              <thead className={`border-b ${S.tblHead}`}>
                <tr>
                  <th className={`font-body text-[10px] tracking-[0.25em] uppercase p-4 ${S.tblHead}`}>{T.sanierung.cols[0]}</th>
                  <th className={`font-body text-[10px] tracking-[0.25em] uppercase p-4 ${S.tblHead}`}>{T.sanierung.cols[1]}</th>
                </tr>
              </thead>
              <tbody>
                {T.sanierung.rows.map(([k, m]) => (
                  <tr key={k} className={`border-b last:border-b-0 ${S.tblRow}`}>
                    <td className={`font-body text-[13px] p-4 align-top ${S.tblRow}`}>{k}</td>
                    <td className={`font-body text-[13px] p-4 ${S.tblRowSub}`}>{m}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        {/* 06 Impressions */}
        <Section n={6} title={T.sections.photos} testId="baum-impressionen" T={S}>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
            {galleryImages.map((im, i) => (
              <button
                key={i}
                type="button"
                onClick={() => lb.open(i)}
                data-testid={`baum-photo-${i}`}
                className="group relative aspect-[4/3] overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-ire-accent"
              >
                <img src={im.src} alt={im.label} loading="lazy" decoding="async" className={`absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04] ${S.photoTint}`} />
                <div className={`absolute inset-0 bg-gradient-to-t ${S.lightboxOverlay} via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity`} />
              </button>
            ))}
          </div>
          <p className={`font-body text-[11px] mt-3 ${S.photoFooter}`}>
            {T.photoDisclaimer}
          </p>
        </Section>

        {/* 07 Location story */}
        <Section n={7} title={T.sections.standort} testId="baum-standort" T={S}>
          <div className={`space-y-6 font-body text-[14px] md:text-[15px] leading-[1.7] ${S.body}`}>
            {T.standort.map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
            {T.stats.map((s) => (
              <div key={s.l} className={`border p-4 ${S.statBox}`}>
                <div className={`font-mono text-2xl tabular-nums ${S.statVal}`}>{s.v}</div>
                <div className={`font-body text-[10px] tracking-[0.2em] uppercase mt-2 ${S.statLbl}`}>{s.l}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* 08 Location */}
        <Section n={8} title={T.sections.lage} testId="baum-lage" T={S}>
          <p className={`font-body text-[14px] mb-4 ${S.body}`}>{T.lage.address}</p>
          <div className={`aspect-[16/10] md:aspect-[21/9] overflow-hidden border ${S.card}`}>
            <iframe
              title="Location Baumhaselring 180"
              src="https://www.openstreetmap.org/export/embed.html?bbox=12.983,52.405,13.006,52.417&layer=mapnik&marker=52.411152,12.994026"
              className={`w-full h-full ${S.mapFilter}`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Section>

        {/* 09 Contact */}
        <Section n={9} title={T.sections.contact} testId="baum-kontakt" T={S}>
          <div className={`border p-5 md:p-8 ${S.card}`}>
            <h3 className={`font-heading text-xl md:text-2xl mb-3 ${S.h2}`}>{T.contact.heading}</h3>
            <p className={`font-body text-[14px] leading-[1.7] mb-6 max-w-xl ${S.body}`}>
              {T.contact.body}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button onClick={() => openCalendly()} data-testid="baum-cta-calendly" className={`inline-flex items-center justify-center gap-3 px-6 py-4 font-body text-[11px] tracking-[0.3em] uppercase transition-colors w-full sm:w-auto ${S.ctaPrimary}`}>
                {T.contact.cta1}
              </button>
              <a href={getWhatsAppUrl(T.contact.waMsg)} target="_blank" rel="noopener noreferrer" data-testid="baum-cta-whatsapp" className={`inline-flex items-center justify-center gap-3 border px-6 py-4 font-body text-[11px] tracking-[0.3em] uppercase transition-colors w-full sm:w-auto ${S.ctaSecondary}`}>
                {T.contact.cta2}
              </a>
            </div>
            <div className={`mt-8 pt-6 border-t ${S.sectionBorder}`}>
              <div className={`font-heading text-lg ${S.contactName}`}>{T.contact.name}</div>
              <div className={`font-body text-[12px] mt-1 ${S.contactTitle}`}>{T.contact.title}</div>
              <div className="mt-3 space-y-1 font-body text-[13px]">
                <a href="mailto:info@ireberlin.com" className={`block hover:underline ${S.linkGold}`}>info@ireberlin.com</a>
                <a href="tel:+491621871064" className={`block ${S.linkDim}`}>+49 162 1871064</a>
              </div>
            </div>
          </div>
        </Section>

        <p className={`font-body text-[11px] leading-[1.6] pt-6 border-t ${S.disclaimer}`}>
          {T.disclaimer}
        </p>
      </div>
    </div>
  );
};

export default BaumhaselringPage;
